import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InvoiceFormDialog from '@/components/admin/accounting/InvoiceFormDialog';
import InvoiceActionsMenu, { useInvoiceWorkflow } from '@/components/admin/accounting/InvoiceWorkflow';
import { getAccountingCustomer, getAccountingItems, getAccountingTaxes } from '@/lib/api';
import { accountingError, invoiceStatusMeta } from '@/lib/accounting';
import { formatDisplayDate } from '@/lib/dates';

export default function StudentDocumentInvoices({ studentId }) {
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true); setError('');
    try { setCustomer((await getAccountingCustomer(`student:${studentId}`)).data); }
    catch (err) { setError(accountingError(err, 'Invoices could not be loaded.')); }
    finally { setLoading(false); }
  }, [studentId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('portal-documents-changed', load);
    window.addEventListener('focus', load);
    return () => { window.removeEventListener('portal-documents-changed', load); window.removeEventListener('focus', load); };
  }, [load]);
  useEffect(() => {
    let active = true;
    Promise.all([getAccountingItems({ context: 'sell', status: 'active', page_size: 100 }), getAccountingTaxes()])
      .then(([products, taxList]) => { if (active) { setItems(products.data?.items || []); setTaxes(taxList.data?.items || taxList.data || []); } })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const changed = useCallback(async () => {
    window.dispatchEvent(new CustomEvent('portal-documents-changed', { detail: { studentId } }));
  }, [studentId]);
  const workflow = useInvoiceWorkflow({ reload: changed, customers: customer ? [customer] : [], items, taxes, paymentMethods: customer?.payment_methods || [] });
  const permissions = customer?.permissions || {};
  return <section aria-label="Student invoices" className="min-w-0 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white/70 p-4">
    <div className="document-row grid gap-3">
      <div className="min-w-0"><h3 className="font-medium text-[#062B1A]">Invoices</h3><p className="mt-1 text-xs text-[#062B1A]/60">Create and manage this student's invoices here. Add another invoice for each separate charge.</p></div>
      <div className="document-row-actions flex flex-wrap items-start gap-2">
        {permissions.create_invoice && <Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-2 size-4" />Create invoice</Button>}
        <Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`mr-2 size-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
      </div>
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    {loading && !customer ? <p className="mt-3 text-xs">Loading invoices…</p> : customer && !customer.invoices?.length ? <p className="mt-3 text-xs text-[#062B1A]/60">No invoices yet.</p> : customer?.invoices?.length > 0 && <div className="mt-4 overflow-x-auto rounded-lg border border-[#062B1A]/10">
      <table className="w-full min-w-[650px] text-left text-xs"><thead className="bg-[#F7F8FA]"><tr>{['Invoice', 'Status', 'Date', 'Total', 'Outstanding', 'Actions'].map(label => <th key={label} className="px-3 py-2">{label}</th>)}</tr></thead><tbody>
        {customer.invoices.map(invoice => <tr key={invoice.ref} className="border-t border-[#062B1A]/10">
          <td className="px-3 py-3 font-medium">{invoice.number}</td><td className="px-3 py-3">{invoiceStatusMeta(invoice.status).label}</td><td className="px-3 py-3">{formatDisplayDate(invoice.issue_date)}</td><td className="px-3 py-3 tabular-nums">{invoice.total_display}</td><td className="px-3 py-3 tabular-nums">{invoice.amount_due_display}</td>
          <td className="px-3 py-2"><div className="flex flex-wrap items-center justify-end gap-2">
            {permissions.edit_invoice && invoice.status !== 'void' && <Button size="sm" variant="outline" onClick={() => workflow.actions.edit(invoice)}>Edit</Button>}
            {invoice.status === 'draft' && permissions.edit_invoice && <Button size="sm" onClick={() => workflow.actions.issue(invoice)}>Confirm invoice</Button>}
            <Button size="sm" variant="outline" onClick={() => workflow.actions.pdf(invoice)}>Download</Button>
            <InvoiceActionsMenu invoice={invoice} permissions={permissions} actions={workflow.actions} />
          </div></td>
        </tr>)}
      </tbody></table>
    </div>}
    <InvoiceFormDialog open={creating} onOpenChange={setCreating} customerRef={`student:${studentId}`} customers={customer ? [customer] : []} items={items} taxes={taxes} onSaved={changed} onSelectExisting={invoice => { setCreating(false); workflow.actions.edit(invoice); }} />
    {workflow.dialogs}
  </section>;
}
